import clsx from 'clsx'
import { replace } from 'lib/routing/appRouting'
import moment from 'moment'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import type { ConnectedProps } from 'react-redux'
import { connect } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { SearchBurnProvider } from 'contexts/SearchPointsContext'
import { pushWithRegion, replaceWithRegion } from 'actions/NavigationActions'
import { fetchPopularHotelFilters } from 'actions/OfferActions'
import { fetchSpecificPlaces, fetchDestinationImageId } from 'actions/PlaceActions'
import { getListName } from 'analytics/enhanced-ecommerce'
import {
  searchOfferListFilters,
  searchResultDealClick,
  searchResultImpressionCount,
  searchSessionDealClick,
} from 'analytics/eventDefinitions'
import { impressionEventWithContext, productClickEventWithContext, searchEvent } from 'analytics/snowplow/events'
import { addGTMEvent } from 'api/googleTagManager'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import {
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import type { SearchFormRef } from 'components/Search'
import type { EnabledFilters } from 'components/Search/type'
import { getAppliedFilterCount } from 'components/Search/utils'
import { saveSearchedPlaces } from 'lib/search/searchedPlaces'
import { mediaQueryUp } from 'components/utils/breakpoint'
import {
  LE_CONTRACTED_HOTEL_TYPES,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_BUNDLE_AND_SAVE,
  OFFER_TYPE_VILLA,
} from 'constants/offer'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import { Experiments } from 'constants/experiments'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON, CHARTER_PAGE_SLUG, MAX_TRAVELLERS_FOR_MALDIVES_CHARTER, MALDIVES_CHARTER_SEARCH_PAGE_MIN_CHECK_IN_DATE, MALDIVES_CHARTER_SEARCH_PAGE_MAX_CHECK_IN_DATE } from 'constants/charterFlight'
import { isMaldivesCharterRegionEnabled } from 'selectors/maldivesSelectors'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import {
  ANYWHERE_NAME,
  ANYWHERE_PLACE_ID,
} from 'constants/search'
import useCustomerRatingOptions from 'hooks/Search/useCustomerRatingOptions'
import useLocationSearchLabels from 'hooks/Search/useLocationSearchLabels'
import type { GlobalSearchAction } from 'contexts/GlobalSearch/GlobalSearchState'
import {
  GLOBAL_SEARCH_INITIAL_STATE,
  GlobalSearchStateActions,
} from 'contexts/GlobalSearch/GlobalSearchState'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useDestinationTags from 'hooks/Search/useDestinationTags'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import {
  createRecentPropertySearchObject,
  createSearchSessionKey,
  decodeSearchParams,
  getDistanceEqFilter,
  getDistanceLteFilter,
  getSearchKey,
  getSearchTypeFromFilters,
  getsScheduleVisibilitiesFilter,
  isAnywhereDestinationSearch,
  isStandardOccupancy,
} from 'lib/search/searchUtils'
import { shouldHideAnytimePrices } from 'lib/search/hideAnytimePrices'
import {
  deleteSearchParams,
  parseSearchString,
  setManySearchParamValues,
  setSearchParamValue,
  validateBoundsSearchParams,
  validateDestinationSearchParams,
  validateLandmarkSearchParams,
  validateLatLngSearchParams,
  validateNaturalLanguageSearchParams,
  validatePropertySearchParams,
} from 'lib/url/searchUrlUtils'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'

import { addRecentSearch } from 'actions/SearchActions'
import { trackClientEvent, trackEvent } from 'analytics/analytics'
import {
  getSearchContextOverrides,
  mapGlobalSearchContextToSnowplowSearchEvent,
  mapOfferListFiltersToFilterContexts, mapOfferListFiltersToSortContext,
} from 'analytics/mapSnowplowSearchTracking'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import LoadingAnimationBox from 'components/Common/LoadingAnimationBox/LoadingAnimationBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import Heading from 'LuxKit/Typography/Heading'
import ContentTagBanner from 'components/Content/ContentTagBanner'
import type { OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList from 'components/OfferList/OfferList'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import RefreshSearchModal from 'components/SearchV2/Components/Mobile/RefreshSearchModal'
import { FlightsClassTypes } from 'constants/flight'
import config from 'constants/config'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import ModalContext from 'contexts/ModalContext'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import type { TrackingOptions } from 'hooks/Offers/useOfferList'
import useOfferList from 'hooks/Offers/useOfferList'
import useOfferListExtras from 'hooks/Offers/useOfferListExtras'
import useOfferListFilters from 'hooks/Offers/useOfferListFilters'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import useOffersWithProperty from 'hooks/Offers/useOffersWithProperty'
import useStreamSearchExpired from 'hooks/Offers/useStreamSearchExpired'
import useExperiment from 'hooks/Experiments/useExperiment'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import useLowViewDestinationsSalesAndCtrRanking from 'hooks/useLowViewDestinationsSalesAndCtrRanking'
import useSalesAndCtrRanking from 'hooks/useSalesAndCtrRanking'
import useSearchEvent from 'hooks/Search/useSearchEvent'
import useQueryParams from 'hooks/useQueryParams'
import { usePlaceById } from 'hooks/usePlace'
import { EmptyArray, without } from 'lib/array/arrayUtils'
import getOfferListKey from 'lib/offer/offerListKey'
import { isLEHotel } from 'lib/offer/offerTypes'
import { SEARCH_STREAMING_PAGE_SIZE } from 'search/initialiseSearchSocket'
import { buildSelectDatesSearchParams } from 'lib/search/selectDatesUtils'
import HotelSearchHeader from './HotelSearchHeader'
import HotelSearchNoSearchResultSection from './HotelSearchNoSearchResultSection'
import MaldivesBanner from './MaldivesBanner'
import OfferCountAndSort from './OfferCountAndSort'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import { getSource, isPropertyCollectEnabled } from 'selectors/offerSelectors'
import { isPropertyBlacklisted } from 'lib/metasearch/metasearchBlacklist'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { excludeNullOrUndefined } from 'checkout/utils'
import { CompactFooter, OriginalFooter } from 'components/App/Footer'
import type {
  FilterControlChangeHandler,
} from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import HotelSearchPageFilterPane from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import Group from '../../utils/Group'
import { useInView } from 'react-intersection-observer'
import OfferListLoadingOfferTile from '../../OfferList/OfferListTiles/OfferListLoadingOfferTile'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import useSortWasApplied from 'hooks/Search/useSortWasApplied'
import useFilterWasApplied from 'hooks/Search/useFilterWasApplied'
import useSearchOfferTileDensityExperiment from 'hooks/Search/useSearchOfferTileDensityExperiment'
import useTrackingSearchEvent from 'hooks/Search/useTrackingSearchEvent'
import { useHideCharterOffersFromSearch } from 'hooks/Search/useHideCharterOffersFromSearch'
import OfferSelectionStickyFooter from 'components/OfferComparison/OfferSelectionStickyFooter'
import uuidV4 from 'lib/string/uuidV4Utils'
import usePropertyTags from '../../../hooks/Offers/usePropertyTags'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import useSearchSession from '../../../hooks/Search/useSearchSession'
import useHotelSearchExclusionContext from 'hooks/Search/useHotelSearchExclusionContext'
import { useSearchBlockingState } from 'hooks/Search/useSearchBlockingState'
import SiteTakeoverSearchBanner from 'takeovers/utils/components/SiteTakeoverSearchBanner'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'
import { getAccommodationSearchHeadMeta } from './utils/getAccommodationSearchHeadMeta'
import { useNextValidCharterPeriod } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useNextValidCharterPeriod'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import usePrevious from '../../../hooks/usePrevious'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { isWhitelabelBrand } from 'lib/whitelabels/whitelabels'
import { getDestinationSearchCanonicalPath } from './utils/getDestinationSearchCanonicalPath'
import { MaldivesDirectBundleProvider, EMPTY_FARE_CLASS_MAP, EMPTY_RESTRICTIONS_MAP } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import { useCheapestBundlePrice } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useCheapestBundlePrice'
import noop from 'lib/function/noop'
import OneClickBookingRestorationTile from './OneClickBookingRestoration/OneClickBookingRestorationTile'
import useOneClickBookingRestoration from './OneClickBookingRestoration/useOneClickBookingRestoration'
import useHiddenPreviewBannerEligibility from './useHiddenPreviewBannerEligibility'
import HiddenPreviewSearchResultBanner from './HiddenPreviewSearchResultBanner'
import useLuxPlusFreePreviewAutoEnrol from 'luxPlus/hooks/useLuxPlusFreePreviewAutoEnrol'
import {
  ALTERNATIVE_OFFER_GROUPING_ROLLOUT,
  useDestinationAlternativeOfferGrouping,
} from './hooks/useDestinationAlternativeOfferGrouping'

const destinationTypes = new Set(['destination', 'naturalLanguage'])
const recentSearchesWhitelist = new Set(['destination', 'landmark', 'property', 'naturalLanguage'])
const ENABLE_SEARCH_VERTICAL_TABS_EXPERIMENT = true

const ANALYTICS_HOTEL_SEARCH_TAG = 'HS'

const PageWrapper = styled(Group)`
  height: calc(var(--vh, 1vh) * 100);
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    display: grid;
    padding-top: ${rem(24)};
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 2fr);
      padding-top: ${rem(12)};
    }
  }

  &.fullWidth {
    padding: ${rem(16)} 0;
  }

`

const HeadingWrapper = styled.div`
  padding-inline-start: ${rem(8)};

  ${mediaQueryUp.tablet} {
    padding-inline-start: 0;
  }
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
`

function mapState(state: App.State) {
  const filters = getUrlOfferListFilters(state)
  return {
    windowSearch: state.router.location.search,
    filters,
    memberId: state.auth.account.memberId,
    searchResultMetadata: state.offer.searchResultMetadata,
    isFlexibleSearch: !!(filters?.flexibleMonths && filters?.flexibleNights),
    specificPlaces: state.destination.specificPlaces[filters.destinationId ?? ''],
    region: state.geo.currentRegionCode,
  }
}

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

interface Props extends PropsFromRedux {}

function HotelSearchPage({
  filters,
  searchResultMetadata,
  windowSearch,
  isFlexibleSearch,
  region,
  specificPlaces,
}: Props) {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const intl = useIntl()
  const { landmarkSearchSortByTypes, searchSortByTypes, SORT_OPTION_CUSTOMER_RATING } = useOfferListSortOptions()
  const locationLabels = useLocationSearchLabels()
  const customerRatingOptions = useCustomerRatingOptions()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const currentPageHash = useAppSelector(state => state.router.location.hash)
  const isMobileScreen = useScreenSizeOnly('mobile')
  const isDesktop = useScreenSizeUp('largeDesktop')

  const searchType = getSearchTypeFromFilters(filters)
  const params = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const searchParams = useMemo(() => decodeSearchParams(windowSearch), [windowSearch])
  const isPerNightPricing = params.priceBy === 'night'
  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])
  const typeaheadId = params.typeaheadId

  const { blockSearch } = useSearchBlockingState({
    extracting: params.extracting,
    typeaheadId,
    query: params.query,
    windowSearch,
    pathname: location.pathname,
  })

  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const bypassRegionSettingId = useAppSelector(state => state.marketing.bypassRegionSettingId)
  const source = useAppSelector(state => getSource(state))
  const pageSize = SEARCH_STREAMING_PAGE_SIZE
  const destinationImageId = useAppSelector(state =>
    filters.destinationId ? state.destination.places[filters.destinationId]?.imageId : undefined,
  )

  const initialPage = useRef<number>(params.page ? parseInt(params.page) : 1)
  const naturalLanguageSearch = config.NATURAL_LANGUAGE_SEARCH_ENABLED
  const shouldShowBanner = useShouldShowBanner({ location: 'search', vertical: 'hotel' })

  // used to determine if the search was blocked by the typeahead extraction
  const previouslyBlockedSearch = usePrevious(params.extracting)

  const searchKey = getSearchKey(params)
  const { searchEvent: searchSessionSearchEvent } = useSearchEvent(searchKey)

  const searchSessionKey = createSearchSessionKey({
    destinationId: params.destinationId,
    propertyId: params.propertyId,
    landmarkId: params.landmarkId,
    lat: params.lat,
    lng: params.lng,
    query: params.query,
  })
  const { searchSessionId } = useSearchSession(searchSessionKey)

  const {
    searchTargetLabel,
    offerDistanceFromSearchTarget,
  } = useMemo(() => ({
    searchTargetLabel: params.query || params.landmarkName || params.destinationName || params.propertyName,
    offerDistanceFromSearchTarget: searchResultMetadata.distanceFromSearchTarget[params.landmarkId || params.destinationId || ''],
  }), [
    params.landmarkId,
    params.landmarkName,
    params.destinationId,
    params.destinationName,
    params.propertyName,
    params.query,
    searchResultMetadata.distanceFromSearchTarget,
  ])

  const destinationId = filters.destinationId
  const propertySpecificSearch = !!filters.propertyId
  const hasOfferTagsApplied = !!(filters.offerTags && filters.offerTags.length > 0)
  const { tags: destinationTags } = useDestinationTags(destinationId)
  useLuxPlusFreePreviewAutoEnrol()
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const isCurrentRegionUserLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const payAtPropertyEnabled = useAppSelector(isPropertyCollectEnabled)
  const propertyTypeFilterEnabled = useFeatureFlag(OptimizelyFeatureFlags.hotelPropertyTypeFilter)
  const canActivateNlsLlmRerank = params.semanticRerank === 'true'
  const { isAnyVariant: isNlsLlmRerankVariant } = useExperiment(
    Experiments.searchNlsLlmRerank,
    { canActivate: canActivateNlsLlmRerank },
  )
  const shouldEnableNlsLlmRerank = canActivateNlsLlmRerank && isNlsLlmRerankVariant
  const { isAnyVariant: isOneClickBookingRestorationExperimentEnabled } = useExperiment(
    Experiments.croOneClickBookingRestoration,
    { canActivate: !isAgentHubEnabled },
  )
  const { experimentVariant: salesCtrRankingVariant, rankingComparisonPending: ctrRankingComparisonPending } = useSalesAndCtrRanking(destinationId)
  const salesCtrRankingEnabled = !!salesCtrRankingVariant
  const {
    experimentVariant: lowViewDestinationsSalesCtrRankingVariant,
    rankingComparisonPending: lowViewDestinationsSalesCtrRankingComparisonPending,
  } = useLowViewDestinationsSalesAndCtrRanking(destinationId)
  const lowViewDestinationsSalesCtrRankingEnabled = !!lowViewDestinationsSalesCtrRankingVariant
  const rankingComparisonPending = ctrRankingComparisonPending || lowViewDestinationsSalesCtrRankingComparisonPending
  const {
    enabled: shouldEnableDestinationAlternativeOfferGrouping,
    pending: destinationAlternativeOfferGroupingProbePending,
  } = useDestinationAlternativeOfferGrouping({
    searchType,
    destinationId,
    propertySpecificSearch,
  })

  useEffect(() => {
    if (filters.destinationId && filters.destinationId !== ANYWHERE_PLACE_ID) {
      dispatch(fetchSpecificPlaces(filters.destinationId))
    }
  }, [dispatch, filters.destinationId])

  useEffect(() => {
    if (destinationImageId || !filters.destinationId || filters.destinationId === ANYWHERE_PLACE_ID) {
      return
    }

    dispatch(fetchDestinationImageId(filters.destinationId))
  }, [dispatch, filters.destinationId, destinationImageId])

  const formRef = useRef<SearchFormRef>(null)
  const onBedbankSelectDatesPrompt = useCallback((offerId: string, offerType: App.OfferType, windowSearch: string) => {
    const paramsToSet = [{ paramName: 'offerType', paramValue: offerType }, { paramName: 'offerId', paramValue: offerId }]
    if (isMobileScreen) {
      toggleSearchVertical('hotels', { withDateSelect: true, search: setManySearchParamValues(windowSearch, paramsToSet) })
    } else {
      navigate({ search: setManySearchParamValues(windowSearch, paramsToSet) }, { replace: true })
      formRef.current?.form.current.scrollIntoView()
      formRef.current?.openDatePickerMenu()
    }
  }, [isMobileScreen, navigate, toggleSearchVertical])
  const queryParams = useQueryParams()

  useIdleTimeReporter(IdleTimeSource.HotelSearchPage)

  const invalidSearchParams = useMemo(() =>
    !validateDestinationSearchParams(params) &&
    !validateLandmarkSearchParams(params) &&
    !validatePropertySearchParams(params) &&
    !validateBoundsSearchParams(params) &&
    !validateLatLngSearchParams(params) &&
    !validateNaturalLanguageSearchParams(params),
  [params])

  useEffect(() => {
    const onSearchUrlPath = location.pathname === `/${region.toLowerCase()}/search`
    if (invalidSearchParams && onSearchUrlPath) {
      dispatch(replaceWithRegion('/hotels'))
    }
  }, [dispatch, invalidSearchParams, location.pathname, region])

  useEffect(() => {
    dispatch(fetchPopularHotelFilters())
  }, [dispatch])

  const maldivesCharterEnabled = useAppSelector(isMaldivesCharterRegionEnabled)

  const { tags: propertyTags, fetching: fetchingPropertyTags } = usePropertyTags(
    filters.propertyId,
    { disabled: !propertySpecificSearch },
  )
  const propertyNotLocationSensitive = useMemo(() =>
    !fetchingPropertyTags && !propertyTags?.includes('location sensitive'),
  [fetchingPropertyTags, propertyTags])

  const flexibleSearch = !!isFlexibleSearch
  const isAnytimeSearch = ((!filters?.checkIn && !filters?.checkOut) && !flexibleSearch)

  const showFlashInNearbyPropertySearch = propertySpecificSearch && !isAnytimeSearch && propertyNotLocationSensitive
  const excludeCharterOffers = useHideCharterOffersFromSearch()

  const listFilters = useMemo((): App.OfferListFilters => {
    let offerTypes: Array<App.OfferListOfferType> = [...LE_CONTRACTED_HOTEL_TYPES, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_BED_BANK]
    if (config.ENABLE_VILLA_RENTALS) {
      offerTypes.push(OFFER_TYPE_VILLA)
    }
    if (filters.offerTypes?.includes('rental')) {
      offerTypes = filters.offerTypes
    } else if (filters.offerTypes?.includes('hotel')) {
      offerTypes = without(offerTypes, OFFER_TYPE_VILLA)
    }

    return {
      ...filters,
      offerTypes,
      distanceEq: getDistanceEqFilter(searchType),
      distanceLte: getDistanceLteFilter(searchType),
      scheduleVisibilities: getsScheduleVisibilitiesFilter({
        isSpoofed,
        isLuxPlusEnabled: luxPlusEnabled,
        canViewLuxPlusBenefits,
      }),
      include: ['nearby'],
      source,
      isStream: true,
      excludeCharterOffers,
      rollout: [
        showFlashInNearbyPropertySearch ? 'showLosFlashInNearby' : undefined,
        payAtPropertyEnabled ? 'payAtPropertyEnabled' : undefined,
        shouldEnableNlsLlmRerank ? 'nlsLlmRerank' : undefined,
        salesCtrRankingEnabled ? 'salesCtrRanking' : undefined,
        lowViewDestinationsSalesCtrRankingEnabled && !salesCtrRankingEnabled ? 'salesCtrRanking' : undefined,
        propertyTypeFilterEnabled ? OptimizelyFeatureFlags.hotelPropertyTypeFilter : undefined,
        propertySpecificSearch || shouldEnableDestinationAlternativeOfferGrouping ? ALTERNATIVE_OFFER_GROUPING_ROLLOUT : undefined,
        'charterPairGrouping',
        bypassRegionSettingId ? 'includeAllRegionsLOS' : undefined,
      ].filter(excludeNullOrUndefined),
    }
  }, [filters, searchType, isSpoofed, luxPlusEnabled, canViewLuxPlusBenefits, source, showFlashInNearbyPropertySearch, payAtPropertyEnabled, shouldEnableNlsLlmRerank, salesCtrRankingEnabled, lowViewDestinationsSalesCtrRankingEnabled, propertyTypeFilterEnabled, propertySpecificSearch, shouldEnableDestinationAlternativeOfferGrouping, excludeCharterOffers, bypassRegionSettingId])

  const listKey = getOfferListKey(listFilters)
  const {
    density: searchTileDensity,
    isCompact: isSearchTileDensityExperimentCompact,
  } = useSearchOfferTileDensityExperiment(listFilters)

  const searchId = useMemo(() => {
    return uuidV4()
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey])

  const globalHotelsSearchDispatchRef = useRef<React.Dispatch<GlobalSearchAction> | null>(null)

  const onAnyHotelSelectDatesPrompt = useCallback((offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary) => {
    const { propertySearchItem, mobileSearchParams } = buildSelectDatesSearchParams(offer)

    if (globalHotelsSearchDispatchRef.current) {
      globalHotelsSearchDispatchRef.current({
        type: GlobalSearchStateActions.SET_SEARCH_ITEM,
        searchItem: propertySearchItem,
      })
    }

    if (isMobileScreen) {
      toggleSearchVertical('hotels', {
        withDateSelect: true,
        search: mobileSearchParams,
      })
    } else {
      formRef.current?.form.current.scrollIntoView()
      formRef.current?.openDatePickerMenu()
    }
  }, [isMobileScreen, toggleSearchVertical])

  const utmBp = useAppSelector(state => state.utm.bp)
  const hideAnytimePrices = shouldHideAnytimePrices(isAnytimeSearch, utmBp)

  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    searchTargetLabel,
    offerDistanceFromSearchTarget,
    onBedbankSelectDatesPrompt,
    onAnyHotelSelectDatesPrompt,
    hideAnytimePrices,
  }, queryParams)

  useEffect(() => {
    globalHotelsSearchDispatchRef.current = globalHotelsSearchDispatch
  }, [globalHotelsSearchDispatch])

  useEffect(() => {
    globalHotelsSearchDispatch({
      type: GlobalSearchStateActions.SET_HIDE_ANYTIME_PRICES_ENABLED,
      hideAnytimePrices,
    })
  }, [hideAnytimePrices, globalHotelsSearchDispatch])

  useEffect(() => {
    globalHotelsSearchDispatch({
      type: GlobalSearchStateActions.SET_OFFER_DISTANCE_FROM_SEARCH_TARGET,
      offerDistanceFromSearchTarget,
    })
  }, [offerDistanceFromSearchTarget, globalHotelsSearchDispatch])

  useEffect(() => {
    globalHotelsSearchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_TARGET_LABEL,
      searchTargetLabel,
    })
  }, [searchTargetLabel, globalHotelsSearchDispatch])

  const searchContextOverrides = useMemo(
    () => getSearchContextOverrides(listFilters),
    [listFilters],
  )

  const searchContext = useMemo(() => {
    return mapGlobalSearchContextToSnowplowSearchEvent({
      ...globalHotelsSearchState,
      occupancies: listFilters.rooms,
      flexibleNights: listFilters.flexibleNights as FLEXIBLE_DURATION_RANGE,
      flexibleMonths: listFilters.flexibleMonths,
      durationRange: listFilters.durationRange as Array<FLEXIBLE_DURATION_RANGE>,
      checkinDate: listFilters.checkIn ? moment(listFilters.checkIn) : undefined,
      checkoutDate: listFilters.checkOut ? moment(listFilters.checkOut) : undefined,
      searchItem: searchParams?.searchItem,
      ...searchContextOverrides,
      eventAnalytics: { searchId },
    })
  }, [
    globalHotelsSearchState,
    listFilters,
    searchParams?.searchItem,
    searchId,
    searchContextOverrides,
  ])

  const filterContexts = useMemo(() => {
    return mapOfferListFiltersToFilterContexts({ offerListFilters: listFilters, CUSTOMER_RATING_GTE: customerRatingOptions.CUSTOMER_RATING_GTE })
  }, [listFilters, customerRatingOptions])

  const sortContext = useMemo(() => {
    return mapOfferListFiltersToSortContext({ offerListFilters: listFilters })
  }, [listFilters])

  const sortWasApplied = useSortWasApplied(listFilters)

  const filterWasApplied = useFilterWasApplied(listFilters)

  const trackingSearchEvent = useTrackingSearchEvent({
    searchSessionSearchEvent: previouslyBlockedSearch ? { type: 'search_bar' } : searchSessionSearchEvent,
    sortWasApplied,
    filterWasApplied,
  })

  const disableOfferList = (propertySpecificSearch && fetchingPropertyTags) || blockSearch || rankingComparisonPending || destinationAlternativeOfferGroupingProbePending

  const offerListFilters = useOfferListFilters(listFilters, { disabled: disableOfferList })

  const minRequiredOffers = useMemo(() => {
    return (parseInt(params.pages ?? '1') * pageSize) || undefined
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [params.pages])

  const trackingOptions = useMemo<TrackingOptions>(() => (
    {
      searchEvent: trackingSearchEvent,
      searchContext,
      filterContexts,
      sortContext,
      searchSessionId,
    }
  ), [trackingSearchEvent, searchContext, filterContexts, sortContext, searchSessionId])

  const hotelOfferList = useOfferList(listFilters, { disabled: disableOfferList, typeaheadId }, trackingOptions)

  const searchListKey = hotelOfferList.key

  const propertyIdForRecentSearch = searchParams.searchItem?.searchType === 'searchItemV3' && 'property' in searchParams.searchItem.searchItem.extractedData
    ? searchParams.searchItem.searchItem.extractedData.property?.propertyId
    : searchParams.searchItem?.value
  const recentSearchOffer = useOffersWithProperty(hotelOfferList.offerIds, propertyIdForRecentSearch)
  useEffect(() => {
    const { searchItem, dates: specificDates, rooms, flexibleSearch } = searchParams

    if (searchItem && recentSearchesWhitelist.has(searchType)) {
      // Only add natural language searches to recent searches once extraction is complete
      const isNaturalLanguageSearch = searchItem.searchType === 'searchItemV3' &&
        searchItem.display.searchType === 'naturalLanguage'

      if (isNaturalLanguageSearch) {
        const isExtractionComplete = !searchItem.searchItem.extracting &&
          !!searchItem.searchItem.extractedData

        if (!isExtractionComplete) {
          return
        }
      }

      const dates = {
        checkIn: specificDates?.checkIn,
        checkOut: specificDates?.checkOut,
        flexibleMonths: flexibleSearch?.flexibleMonths,
        flexibleDuration: flexibleSearch?.flexibleNights,
        isAnytime: !(specificDates?.checkIn || specificDates?.checkOut || flexibleSearch?.flexibleMonths || flexibleSearch?.flexibleNights),
      }

      let recentSearchItem: App.SearchItem = searchItem

      // Handle V1 property search
      if (searchType === 'property' && recentSearchOffer) {
        recentSearchItem = createRecentPropertySearchObject(searchItem, recentSearchOffer)
      }

      // Handle V3 property search - enrich with offerId and imageId
      if (
        searchItem.searchType === 'searchItemV3' &&
        searchItem.display.searchType === 'property' &&
        recentSearchOffer
      ) {
        recentSearchItem = {
          ...searchItem,
          display: {
            ...searchItem.display,
            offerId: isLEHotel(recentSearchOffer) ? recentSearchOffer.id : undefined,
            imageId: recentSearchOffer.image.id,
          },
        }
      }

      dispatch(addRecentSearch(recentSearchItem, dates, rooms))
    }
  }, [dispatch, recentSearchOffer, searchParams, searchType])

  useEffect(() => {
    const placeId = params.destinationId ?? params.landmarkId
    if (placeId) {
      saveSearchedPlaces(placeId.split(','))
    }
  }, [params.destinationId, params.landmarkId])

  const deprecatedSearchSessionId = useAppSelector(state => state.analytics.search.sessions[searchListKey])

  const listDepth = useRef<number>(0)
  useEffect(() => {
    return () => {
      if (hotelOfferList.offerIds.length) {
        // On a new search, finalise tracking the depth reached on the old search
        addGTMEvent(searchResultImpressionCount(hotelOfferList.offerIds.length, listDepth.current))
        // event sent, now reset the depth we're at. They probably applied a filter and will be at the top again
        listDepth.current = 0
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [searchListKey])

  useEffect(() => {
    if (listFilters) {
      addGTMEvent(searchOfferListFilters(listFilters))
    }
  }, [listFilters])

  const tracking = useOfferListTracking(
    getListName('OfferList', ANALYTICS_HOTEL_SEARCH_TAG, 'Search Page Offer List'),
    {
      snowplow: {
        enabled: false,
      },
      key: getOfferListKey(listFilters),
    },
  )

  const sortOptions = useMemo(() => {
    const options = searchType === 'landmark' ? landmarkSearchSortByTypes : searchSortByTypes

    return [...options, SORT_OPTION_CUSTOMER_RATING]
  }, [searchType, SORT_OPTION_CUSTOMER_RATING, landmarkSearchSortByTypes, searchSortByTypes])

  const showModal = useContext(ModalContext)
  const streamedExtras = useOfferListExtras(listFilters)
  const searchExpired = useStreamSearchExpired(listFilters)

  useEffect(() => {
    if (searchExpired) {
      trackClientEvent({
        subject: 'expired',
        action: 'show',
        category: 'search',
        type: 'nonInteraction',
      })

      showModal(<RefreshSearchModal />)
    }
  }, [searchExpired, showModal])

  const {
    destinationPlace,
    isMaldivesSearch,
  } = useHotelSearchExclusionContext({
    destinationId,
    destinationName: params.destinationName,
    offerIds: hotelOfferList.offerIds,
    offerListFetching: disableOfferList || hotelOfferList.fetching,
    propertySpecificSearch,
  })
  const isPricePerNightToggleEnabled = false
  const showPerNightPricing = !isSearchTileDensityExperimentCompact && isPricePerNightToggleEnabled && isPerNightPricing

  const mainListFetching = hotelOfferList.fetching || blockSearch || rankingComparisonPending || destinationAlternativeOfferGroupingProbePending

  const Footer = config.footer.type === 'original' ? OriginalFooter : CompactFooter

  const [showFooter, setShowFooter] = useState<boolean>(false)
  const [overrideOfferListFetching, setOverrideOfferListFetching] = useState<boolean>(false)
  const [numberOfInitialLoadingTilesToDisplay, setNumberOfInitialLoadingTilesToDisplay] = useState<number>(() => {
    return (params.pages ? parseInt(params.pages) : 1) * pageSize
  })

  // Snap user-selected dates to the nearest valid charter period (Tuesday check-in);
  // ignored in anytime mode. Also provides maldivesNights for duration/pricing.
  const { fallbackCheckinDate, maldivesNights, effectiveReturnDate } = useNextValidCharterPeriod({
    checkIn: listFilters.checkIn,
    checkOut: listFilters.checkOut,
  })

  // Create bundle version of windowSearch with fallback checkIn and checkOut dates
  // Only include dates when user has selected specific dates; omit for anytime so
  // MaldivesDirectSearchPage can independently find the cheapest date.
  const bundleWindowSearch = useMemo(() => {
    const paramsToSet: Array<{ paramName: string; paramValue: string }> = []

    if (listFilters.checkIn && fallbackCheckinDate && effectiveReturnDate) {
      paramsToSet.push(
        { paramName: 'checkIn', paramValue: fallbackCheckinDate },
        { paramName: 'checkOut', paramValue: effectiveReturnDate },
      )
    }

    if (maldivesNights === 12) {
      paramsToSet.push({ paramName: 'bundle', paramValue: '12-nights' })
    }

    if (paramsToSet.length === 0) return windowSearch

    return setManySearchParamValues(windowSearch, paramsToSet)
  }, [windowSearch, listFilters.checkIn, fallbackCheckinDate, effectiveReturnDate, maldivesNights])

  const isBundleMode = isMaldivesSearch && maldivesCharterEnabled

  const isBelowMaxTravellers = useMemo(
    () => (listFilters.rooms ? sumUpOccupancies(listFilters.rooms) : 0) <= MAX_TRAVELLERS_FOR_MALDIVES_CHARTER,
    [listFilters.rooms],
  )

  // Check if user's check-in date is within valid charter period
  const isCheckInWithinCharterPeriod = useMemo(() => {
    if (!listFilters.checkIn) return true
    return listFilters.checkIn >= MALDIVES_CHARTER_SEARCH_PAGE_MIN_CHECK_IN_DATE &&
      listFilters.checkIn <= MALDIVES_CHARTER_SEARCH_PAGE_MAX_CHECK_IN_DATE
  }, [listFilters.checkIn])

  const charterBundleOfferIds = useAppSelector(state => {
    return hotelOfferList.offerIds.filter(offerId => {
      const offer = state.offer.offers[offerId]
      return offer ? !!getCharterBundleRoute(state, offer) : false
    })
  }, shallowEqual)

  const charterExcludedOfferIds = useMemo(() => {
    if (isBelowMaxTravellers && isCheckInWithinCharterPeriod) return undefined
    if (!propertySpecificSearch) return charterBundleOfferIds

    const indexOfNearbySeparator = hotelOfferList.extras?.find((extra: App.OfferListExtra) => {
      return extra.type === 'nearby'
    })?.position
    if (!indexOfNearbySeparator) return charterBundleOfferIds

    const idsAfterSeparator = new Set(hotelOfferList.offerIds.slice(indexOfNearbySeparator))
    return charterBundleOfferIds.filter(id => idsAfterSeparator.has(id))
  }, [charterBundleOfferIds, isBelowMaxTravellers, isCheckInWithinCharterPeriod, propertySpecificSearch, hotelOfferList.extras, hotelOfferList.offerIds])

  // GHA CUG metasearch: hide blacklisted properties (previously seen as top offers on metasearch)
  // from search results to prevent price mismatches between CUG search prices and B2C partner page prices
  const metasearchBlacklist = useAppSelector(state => state.marketing.metasearchPropertyBlacklist)
  const metasearchBlacklistIds = useMemo(() => {
    if (utmBp !== '3') return []

    return Object.keys(metasearchBlacklist).filter(id =>
      isPropertyBlacklisted(metasearchBlacklist, id) &&
      hotelOfferList.offerIds.includes(id),
    )
  }, [utmBp, metasearchBlacklist, hotelOfferList.offerIds])

  const oneClickBookingRestorationEnabled = !isAgentHubEnabled && isOneClickBookingRestorationExperimentEnabled
  const oneClickBookingRestoration = useOneClickBookingRestoration(listFilters, oneClickBookingRestorationEnabled)

  const searchExcludedIds = useMemo(() => {
    const combined = [
      ...(charterExcludedOfferIds ?? []),
      ...metasearchBlacklistIds,
      ...(oneClickBookingRestoration.offerId ? [oneClickBookingRestoration.offerId] : []),
    ]
    return combined.length > 0 ? combined : undefined
  }, [charterExcludedOfferIds, oneClickBookingRestoration.offerId, metasearchBlacklistIds])

  const hiddenPreviewBannerEligibility = useHiddenPreviewBannerEligibility({
    listFilters,
    mainOfferIds: hotelOfferList.offerIds,
    excludedOfferIds: searchExcludedIds,
    mainSearchBlocked: mainListFetching,
  })
  const isHiddenPreviewAnywhereDestination = !!(
    hiddenPreviewBannerEligibility.firstDestinationId &&
    isAnywhereDestinationSearch(hiddenPreviewBannerEligibility.firstDestinationId)
  )
  const hiddenPreviewPlace = usePlaceById(
    hiddenPreviewBannerEligibility.firstDestinationId,
    !hiddenPreviewBannerEligibility.firstDestinationId || isHiddenPreviewAnywhereDestination,
  )
  const hiddenPreviewDestinationName = isHiddenPreviewAnywhereDestination ? undefined : hiddenPreviewPlace?.name ?? params.destinationName
  const { isAnyVariant: isHiddenPreviewBannerVariant } = useExperiment(
    Experiments.searchHiddenPreviewBannerSearchResult,
    { canActivate: hiddenPreviewBannerEligibility.canActivateExperiment },
  )
  const showHiddenPreviewBanner = hiddenPreviewBannerEligibility.canActivateExperiment && isHiddenPreviewBannerVariant

  const bundleRooms = listFilters.rooms ?? EmptyArray as Array<App.Occupants>

  const bundleContextValue = useMemo(() => ({
    charterBundle: isBundleMode && isBelowMaxTravellers && isCheckInWithinCharterPeriod,
    isPricePerPerson: true,
    fareClass: FlightsClassTypes.ECONOMY,
    duration: maldivesNights,
    allowedFareClassesByOfferId: EMPTY_FARE_CLASS_MAP,
    hasFareClassRestrictionsByOfferId: EMPTY_RESTRICTIONS_MAP,
    canOpenFareClassModal: false,
    openFareClassModal: noop,
    hasUserSelectedDates: false,
  }), [isBundleMode, isBelowMaxTravellers, isCheckInWithinCharterPeriod, maldivesNights])

  // Get banner price via independent light API (non-stream) to ensure accurate lowest price
  const hasSelectedDates = !!(isBundleMode && listFilters.checkIn && fallbackCheckinDate && effectiveReturnDate)
  const bannerBundleData = useCheapestBundlePrice({
    checkIn: fallbackCheckinDate ?? '',
    nights: maldivesNights,
    rooms: bundleRooms,
    fareClass: FlightsClassTypes.ECONOMY,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    disabled: !hasSelectedDates,
  })

  const { maldivesBundlePrice, isMaldivesBundlePriceLoading } = useMemo(() => {
    if (!isBundleMode || !hasSelectedDates) {
      return { maldivesBundlePrice: MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON, isMaldivesBundlePriceLoading: false }
    }
    if (bannerBundleData.fetching) {
      return { maldivesBundlePrice: MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON, isMaldivesBundlePriceLoading: true }
    }
    if (bannerBundleData.available && bannerBundleData.pricePerPerson > 0) {
      const pp = bannerBundleData.memberPricePerPerson ?? bannerBundleData.pricePerPerson
      return { maldivesBundlePrice: pp, isMaldivesBundlePriceLoading: false }
    }
    return { maldivesBundlePrice: MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON, isMaldivesBundlePriceLoading: false }
  }, [isBundleMode, hasSelectedDates, bannerBundleData])

  const onPageChange = useCallback((page: number) => {
    dispatch(replace({ search: setSearchParamValue(windowSearch, 'pages', page), hash: currentPageHash }))
  }, [windowSearch, currentPageHash, dispatch])

  const handleViewBundlesClick = useCallback(() => {
    dispatch(pushWithRegion(`/campaign/${CHARTER_PAGE_SLUG}`, bundleWindowSearch))
  }, [dispatch, bundleWindowSearch])

  const [headerRef, isInView] = useInView()

  const onEndReached = useCallback(() => {
    setShowFooter(true)
  }, [])

  useEffect(() => {
    setShowFooter(false)
  }, [searchKey])

  useEffect(() => {
    // when the search key changes and the main list is not fetching, we scroll the user to the top of the page
    if (mainListFetching) {
      setOverrideOfferListFetching(true)
      return
    }

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [searchKey, mainListFetching])

  useEffect(() => {
    if (!mainListFetching && isInView) {
      setOverrideOfferListFetching(false)
    }
  }, [isInView, mainListFetching])

  useEffect(() => {
    /**
     * Updates the number of loading tiles to display for the next search when:
     * 1. The current search has completed (not fetching)
     * 2. The pages parameter has changed
     *
     * Logic:
     * - Calculate tiles based on current page number * page size
     * - Cap at maximum of 10 tiles to prevent excessive loading states
     */
    if (!mainListFetching && !overrideOfferListFetching) {
      const pageNumber = params.pages ? parseInt(params.pages) : 1
      const maxNumberOfTilesToShow = 10

      setNumberOfInitialLoadingTilesToDisplay(
        Math.min(pageNumber * pageSize, maxNumberOfTilesToShow),
      )
    }
  }, [mainListFetching, overrideOfferListFetching, pageSize, params.pages])

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [dispatch])

  const handlePerNightPricingChange = useCallback((enabled: boolean) => {
    dispatch(replace({
      search: setManySearchParamValues(windowSearch, [{
        paramName: 'priceBy',
        paramValue: enabled ? 'night' : undefined,
      }]),
      hash: currentPageHash,
    }))
  }, [currentPageHash, dispatch, windowSearch])

  const mainListTotal = hotelOfferList.offerCount ?? 0
  // Adjusted total for display, excluding charter offers when over max travellers
  const displayListTotal = mainListTotal - (searchExcludedIds?.length ?? 0)
  const totalOffersForMeta = mainListTotal || hotelOfferList.offerIds?.length || 0

  const searchHeadMeta = useMemo(() => getAccommodationSearchHeadMeta({
    intl,
    searchItem: searchParams.searchItem,
    destinationId: listFilters.destinationId,
    destinationName: listFilters.destinationName,
    totalCount: totalOffersForMeta,
    specificPlaces,
    destinationImageId,
  }), [
    intl,
    searchParams.searchItem,
    listFilters.destinationId,
    listFilters.destinationName,
    totalOffersForMeta,
    specificPlaces,
    destinationImageId,
  ])

  const canonical = useMemo(() => {
    if (searchParams.searchItem?.searchType !== 'destination') {
      return undefined
    }

    return getDestinationSearchCanonicalPath(destinationPlace)
  }, [searchParams.searchItem?.searchType, destinationPlace])

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    // we only want to fire this event on the main offer list
    // and not the extra offer list to avoid dup events
    if (dispatchAction.type === OfferListEvents.listLoad && searchListKey === dispatchAction.key) {
      trackEvent(searchEvent('offer-list-load', 'hotels-search-page', {
        filters: listFilters,
        searchItem: searchParams.searchItem,
        checkIn: listFilters.checkIn,
        checkOut: listFilters.checkOut,
        vertical: 'hotels',
        occupancies: listFilters.rooms,
      }))
    }
    if (dispatchAction.type === OfferListEvents.productClick) {
      const { offer, position, price, memberPrice, duration, extendedAvailability, availability } = dispatchAction
      addGTMEvent(searchResultDealClick())
      addGTMEvent(searchSessionDealClick(deprecatedSearchSessionId, offer.id))
      trackEvent(
        productClickEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration, searchContext, filterContexts, sortContext },
        ),
      )
      if (extendedAvailability) {
        trackClientEvent({
          subject: 'clicked',
          action: `${extendedAvailability}`,
          category: 'extended-window',
          type: 'interaction',
        })
      }
    } else if (dispatchAction.type === OfferListEvents.productImpression) {
      const { offer, position, availability, price, memberPrice, duration, extendedAvailability, roomRateId } = dispatchAction
      listDepth.current = Math.max(position + 1, listDepth.current)
      trackEvent(
        impressionEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration, searchContext, roomRateId, filterContexts, sortContext },
        ),
      )
      if (extendedAvailability) {
        trackClientEvent({
          subject: 'viewed',
          action: `${extendedAvailability}`,
          category: 'extended-window',
          type: 'interaction',
        })
      }
    }
  }, [
    deprecatedSearchSessionId,
    tracking.listName,
    tracking.listId,
    searchContext,
    searchListKey,
    listFilters,
    searchParams.searchItem,
    filterContexts,
    sortContext,
  ])

  const isZSR = (
    destinationTypes.has(searchType) &&
    mainListTotal === 0 && !mainListFetching
  )

  const enabledFilters = useMemo((): EnabledFilters => (
    {
      inclusions: true,
      amenities: true,
      customerRating: true,
      priceFilter: config.ENABLE_HOTEL_SEARCH_PAGE_PRICE_FILTER,
      holidayTypes: searchTargetLabel !== ANYWHERE_NAME,
      propertyTypes: searchTargetLabel !== ANYWHERE_NAME,
      locations: searchTargetLabel == ANYWHERE_NAME,
      offerTypes: config.ENABLE_VILLA_RENTALS,
      popularFilters: true,
      luxPlusFeatures: luxPlusEnabled,
      agentHubFeatures: isAgentHubEnabled,
      loyalty: isCurrentRegionUserLoyaltyRegion,
      campaigns: isCurrentRegionUserLoyaltyRegion,
      bedrooms: true,
      beds: true,
      paymentOptions: payAtPropertyEnabled,
      chains: true,
    }
  ), [searchTargetLabel, luxPlusEnabled, isCurrentRegionUserLoyaltyRegion, payAtPropertyEnabled])

  const filterCount = useMemo(() => {
    return getAppliedFilterCount(listFilters, enabledFilters)
  }, [listFilters, enabledFilters])

  const hasFiltersApplied = filterCount > 0

  if (invalidSearchParams) {
    return null
  }

  const positionOfFirstSeparator = streamedExtras.length > 0 ? streamedExtras[0].position : undefined

  const noExactMatchesForPropertySearch = !mainListFetching && positionOfFirstSeparator === 0

  const showFilterPane = isDesktop &&
    (mainListTotal > 0 ||
      mainListFetching ||
      hasFiltersApplied ||
      ((searchType === 'property' || listFilters?.propertyId) && !noExactMatchesForPropertySearch)
    )

  const offerListFiltersProps = {
    sortOptions,
    searchType,
    switchView: {
      label: intl.formatMessage({
        id: '3HAXL_T9o9_xuqIkoF_O',
        defaultMessage: 'Map view',
        description: 'Form field label for switch to map view on hotel search page',
      }),
      view: 'map' as const,
      URL: mapViewURL,
    },
    listFilters: listFilters,
  }

  const sortByPrice = listFilters.sortBy === 'price.asc' || listFilters.sortBy === 'price.desc'
  const sortByValue = listFilters.sortBy === 'value'
  const showLoadingAnimation = mainListFetching && !isAnytimeSearch && (sortByPrice || sortByValue)

  const numberOfInvalidOffers = hotelOfferList.numberOfInvalidOffers
  const numberOfValidOffers = hotelOfferList.numberOfValidOffers

  const isLifestyleBrand = isWhitelabelBrand(config.BRAND)
  const showMaldivesTopBanner = isMaldivesSearch && maldivesCharterEnabled && !propertySpecificSearch && isBelowMaxTravellers && !isLifestyleBrand && !excludeCharterOffers
  const showMaldivesNearbyBanner = propertySpecificSearch && isMaldivesSearch && maldivesCharterEnabled && isBelowMaxTravellers && !isLifestyleBrand && !excludeCharterOffers

  const streamedExtraElements = streamedExtras.reduce<Array<OfferListV2Item>>((items, extra) => {
    items.push({
      id: extra.id,
      position: extra.position,
      element: (
        <HeadingWrapper>
          <Heading variant="heading4">{extra.heading}</Heading>
        </HeadingWrapper>
      ),
      show: 'hasOffers' as const,
    })

    if (showMaldivesNearbyBanner && extra.id === 'nearby-offers') {
      items.push({
        id: 'maldives-banner-nearby',
        position: extra.position,
        element: <MaldivesBanner
          onViewBundlesClick={handleViewBundlesClick}
          price={maldivesBundlePrice}
          nights={maldivesNights}
          isPriceLoading={isMaldivesBundlePriceLoading}
        />,
        show: 'hasOffers' as const,
      })
    }

    return items
  }, [])

  const extras: Array<OfferListV2Item> = [{
    id: 'no-results',
    position: 'start',
    element: <HotelSearchNoSearchResultSection searchType={searchType} />,
    show: (noExactMatchesForPropertySearch || isZSR),
  },
  {
    id: 'heading',
    position: 'start',
    element: <OfferCountAndSort
      destinationName={params.destinationName}
      specificPlaces={specificPlaces}
      fetching={mainListFetching}
      mainListTotal={displayListTotal}
      sortOptions={sortOptions}
      mapViewUrl={mapViewURL}
      isFlexible={flexibleSearch}
      filters={listFilters}
      windowSearch={windowSearch}
      shouldShowFilters={!showFilterPane && !offerListFilters.fetching}
      showHolidayTypes={searchTargetLabel !== ANYWHERE_NAME}
      query={params.query}
      numberOfInvalidOffers={numberOfInvalidOffers}
      numberOfValidOffers={numberOfValidOffers}
      searchType={listFilters.propertyId ? 'property' : searchType}
      enabledFilters={enabledFilters}
      showPerNightPricingToggle={!isSearchTileDensityExperimentCompact && isPricePerNightToggleEnabled}
      isPerNightPricing={isPerNightPricing}
      onPerNightPricingChange={handlePerNightPricingChange}
      onFilterChange={handleFilterChange}
      onSortChange={handleSortChange}
      enableSearchVerticalTabsExperiment={ENABLE_SEARCH_VERTICAL_TABS_EXPERIMENT}
    />,
    show: (!isZSR),
  },
  {
    id: 'promotional-banner',
    position: 'start',
    element: destinationId ? <ContentTagBanner
      page="search"
      destinationId={destinationId}
    /> : null,
    show: !isZSR && (destinationTags.length > 0 || hasOfferTagsApplied),
  },
  {
    id: 'loading-animation',
    position: 'start',
    element: <LoadingAnimationBox variant="hotels" />,
    show: showLoadingAnimation,
  },
  {
    id: 'scrolling-loading',
    position: 'start',
    element: <>
      {Array.from({ length: numberOfInitialLoadingTilesToDisplay || 4 }).map((_, index) => (
        <OfferListLoadingOfferTile
          key={`loading-tile-${index}`}
          tileStyle="search"
          offerType="hotel"
          className="offer-tile"
          filters={listFilters}
          offerPosition={index}
        />
      ))}
    </>,
    show: overrideOfferListFetching ? 'always' : false,
  },
  ...streamedExtraElements,
  {
    id: 'maldives-banner',
    position: 0,
    element: <MaldivesBanner
      onViewBundlesClick={handleViewBundlesClick}
      price={maldivesBundlePrice}
      nights={maldivesNights}
      isPriceLoading={isMaldivesBundlePriceLoading}
    />,
    show: showMaldivesTopBanner,
  },
  {
    id: 'one-click-booking-restoration',
    position: 0,
    element: oneClickBookingRestoration.offerId && oneClickBookingRestoration.transactionKey && oneClickBookingRestoration.filters ? <OneClickBookingRestorationTile
      offerId={oneClickBookingRestoration.offerId}
      transactionKey={oneClickBookingRestoration.transactionKey}
      filters={oneClickBookingRestoration.filters}
      searchTileDensity={searchTileDensity}
      showPerNightPricing={showPerNightPricing}
    /> : null,
    show: !!(oneClickBookingRestoration.offerId && oneClickBookingRestoration.transactionKey && oneClickBookingRestoration.filters),
  },
  {
    id: 'hidden-preview-banner',
    position: 1,
    element: <HiddenPreviewSearchResultBanner
      destinationName={hiddenPreviewDestinationName}
      savingPercent={hiddenPreviewBannerEligibility.savingPercent}
    />,
    show: showHiddenPreviewBanner ? 'hasOffers' as const : false,
  },
  ]

  return <PageWrapper direction="vertical" verticalAlign="space-between" gap={16}>
    <div>
      <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
        <GlobalSearchContextProvider
          state={globalHotelsSearchState}
          dispatch={globalHotelsSearchDispatch}
        >
          <HeadData
            noIndex
            title={searchHeadMeta.title}
            description={searchHeadMeta.description}
            imageId={searchHeadMeta.imageId}
            imageUrl={searchHeadMeta.imageUrl}
            imageAlt={searchHeadMeta.imageAlt}
            canonical={canonical}
          />
          {shouldShowBanner && <SiteTakeoverSearchBanner vertical="hotel" />}
          <div ref={headerRef} />
          <HotelSearchHeader
            filters={listFilters}
            formRef={formRef}
            offerListFiltersProps={offerListFiltersProps}
            searchVertical="hotels"
            baseSearchPath="/search"
            baseSearchMapPath="/search/map"
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
            locationSearchPlaceholder={naturalLanguageSearch ? locationLabels.placeholderLabelNaturalLanguage : locationLabels.placeholderLabel}
            label={naturalLanguageSearch ? locationLabels.titleLabelNaturalLanguage : locationLabels.titleLabel}
            showNaturalLanguageSearchSuggestions={naturalLanguageSearch}
          />
          <ErrorBoundary fallback={null}>
            <PromptCardSelector tripReengagement />
          </ErrorBoundary>
          <SearchBurnProvider>
            <MaldivesDirectBundleProvider value={bundleContextValue}>
              <StyledLayoutContainer
                size={showFilterPane ? 'xl' : 'lg'}
                className={clsx({ filterPane: showFilterPane, fullWidth: isMobileScreen })}
              >
                <StyledVerticalSpacer gap={16}>
                  <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
                    <OfferList
                      filters={listFilters}
                      interstitials={extras}
                      onPageChange={onPageChange}
                      tileStyle="search"
                      eagerLoadFirstImage
                      defaultPage={initialPage.current}
                      fullWidth={isMobileScreen}
                      pageSize={pageSize}
                      clientCheckAvailability
                      searchId={searchId}
                      showLoadingTiles={!showLoadingAnimation}
                      onEndReached={onEndReached}
                      overrideFetching={overrideOfferListFetching}
                      minRequiredOffers={minRequiredOffers}
                      disableUseOfferList={disableOfferList}
                      excludedIds={searchExcludedIds}
                      typeaheadId={typeaheadId}
                      showPerNightPricing={showPerNightPricing}
                      searchTileDensity={searchTileDensity}
                    />
                  </OfferListEventsProvider>
                </StyledVerticalSpacer>
                {showFilterPane && <HotelSearchPageFilterPane
                  filters={listFilters}
                  mapViewURL={mapViewURL}
                  search={windowSearch}
                  enabledFilters={enabledFilters}
                  hotelOfferList={hotelOfferList}
                  mainListFetching={mainListFetching}
                  doNotScrollToTopOfSelection
                  showLoadingSkeletonOnEachFilterSelection
                  bedroomsFilterGroupAtBottom={isStandardOccupancy(listFilters)}
                  onFilterChange={handleFilterChange}
                  enableSearchVerticalTabsExperiment={ENABLE_SEARCH_VERTICAL_TABS_EXPERIMENT}
                />}
              </StyledLayoutContainer>
            </MaldivesDirectBundleProvider>
          </SearchBurnProvider>
          <OfferSelectionStickyFooter />
        </GlobalSearchContextProvider>
      </AnalyticsPageContext.Provider>
    </div>
    {showFooter && <Footer />}
  </PageWrapper>
}

export default connector(HotelSearchPage)
